/*============================================================================*/

/**
 * Formatter functions
 *
 * Functions that transform or structure raw data into usable formats,
 * such as building URLs, shaping objects, or preparing values for use.
 * They do not perform side effects beyond formatting.
 */

/*============================================================================*/


/**
 * Data formatter function
 *
 * Inserts "buy again" URLs into an array of order nodes.
 * Processes the raw line items in each order, generates the
 * appropriate URL based on environment, and attaches it to
 * the order object as `buyAgainUrl`.
 *
 * @param {Object} params - Function parameters
 * @param {Array} params.ordersNodes - Array of order nodes to process
 */
function insertBuyAgainUrlsInOrdersNode({ ordersNodes })
{
    ordersNodes.forEach((order) =>
    {
        const lineItems = order?.lineItems?.edges || []

        const variants = []
        const quantities = []

        lineItems.forEach((itemEdge) =>
        {
            const item = itemEdge.node
            const variantGid = item?.variant?.id
            const quantity = item?.quantity

            if (variantGid && quantity > 0 && item?.product)
            {
                const variantId = variantGid.split('/').pop()
                variants.push(variantId)
                quantities.push(quantity)
            }
        })

        if (variants.length > 0)
        {
            const queryParams = new URLSearchParams()
            queryParams.set('autocart', 'true')
            queryParams.set('product_variant', variants.join(','))
            queryParams.set('product_quantity', quantities.join(','))

            const shopURL = environment === 'staging'
                ? "https://the-malediction.myshopify.com"
                : "https://shop.malediction.gg"

            order.buyAgainUrl = `${shopURL}/products?${queryParams.toString()}`
        }
        else
        {
            order.buyAgainUrl = null
        }
    })
}

/**
 * Escapes special HTML characters into their corresponding entities.
 *
 * @param {Object} params
 * @param {string} params.str - The input string to escape.
 * @returns {string} Escaped string safe to inject into HTML.
 */
function escapeHTML({ str = '' })
{
    return str.replace(/[&<>'"]/g, (tag) =>
    {
        const chars = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            "'": '&#39;',
            '"': '&quot;'
        };
        return chars[tag] || tag;
    });
}

/**
 * Unescapes HTML entities back into their original characters.
 *
 * @param {Object} params
 * @param {string} params.str - The input string containing HTML entities.
 * @returns {string} Unescaped string with original characters restored.
 */
function unescapeHTML({ str = '' })
{
    const entities = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&#39;': "'",
        '&quot;': '"'
    };

    return str.replace(/(&amp;|&lt;|&gt;|&#39;|&quot;)/g, (entity) =>
    {
        return entities[entity] || entity;
    });
}

/**
 * Sanitizes a string for safe use in HTML by escaping special characters,
 * removing dangerous tags and attributes.
 *
 * @param {Object} params
 * @param {string} params.str - The input string to sanitize.
 * @returns {string} Sanitized string safe for injection into HTML.
 */
function sanitizeHTML({ str = '' })
{
    let sanitized = escapeHTML({ str });

    sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    sanitized = sanitized.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    sanitized = sanitized.replace(/\son\w+="[^"]*"/gi, '');
    sanitized = sanitized.replace(/\son\w+='[^']*'/gi, '');

    return sanitized;
}

/**
 * Format a date into a localized string according to the user's language.
 *
 * - Safely parses the input date.
 * - Returns a string formatted with `Intl.DateTimeFormat` options.
 *
 * @param {Object} options
 * @param {string|Date|number} options.dateInput - Date to be formatted.
 * @param {string} [options.locale=navigator.language] - Locale for formatting.
 * @param {Object} [options.options={}] - Intl formatting options (merged with defaults).
 * @returns {string} Localized date string or empty string if invalid.
 */
function formatDate({ dateInput, locale = navigator.language, options = {} } = {})
{
    if (!dateInput) return '';

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        ...options
    });
}

/**
 * Format a numeric value into a localized currency string
 *
 * @param {number|string} amount - The amount to format (can be number or numeric string).
 * @param {Object} [options={}] - Formatting options.
 * @param {boolean} [options.isCents=false] - If true, treats the value as cents and converts to units.
 * @param {string|null} [options.currency=null] - Currency code (e.g., "USD", "BRL"). 
 *   If null, tries to resolve from Shopify or from <meta name="shop-currency"> tag.
 * @returns {string} The formatted currency string.
 * @throws {Error} If the provided amount is not a valid number.
 */
function formatMoney({ amount, isCents = false, currency = null } = {})
{
    const locale =
        (typeof Shopify !== 'undefined' && Shopify.locale) ||
        document.documentElement.lang ||
        navigator.language ||
        'en-US';

    let resolvedCurrency = currency;
    if (!resolvedCurrency)
    {
        const meta = document.querySelector('meta[name="shop-currency"]');
        resolvedCurrency = meta?.content || 'USD';
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount))
    {
        throw new Error(`formatMoney: Invalid amount "${amount}"`);
    }

    const amountInUnits = isCents ? numericAmount / 100 : numericAmount;

    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: resolvedCurrency,
    }).format(amountInUnits);
}

/**
 * Cleans and extracts the raw numeric ID from a Shopify Global ID (gid://) or a URL parameter string.
 * @param {string|number} id - The raw ID string or number (e.g., 'gid://shopify/ProductVariant/123456789' or '123456?pr=1').
 * @returns {string|null} The sanitized, numeric Shopify ID as a string, or null if input is falsy.
 */
const cleanShopifyId = function(id)
{
    if (!id) return null;
    const str = String(id).split('?')[0];
    return str.includes('gid://') ? str.split('/').pop() : str;
};

/**
 * Ensures a given price value is strictly formatted as integer cents.
 * Handles discrepancies between Shopify's dollar and cent formatting outputs.
 * @param {number|string} price - The raw price value to evaluate.
 * @returns {number} The normalized price in cents.
 */
const ensureCents = function(val)
{
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return val;
    if (String(val).indexOf('.') !== -1)
    {
        return Math.round(parseFloat(val) * 100);
    }
    return parseInt(val);
};

/**
 * Converts all line breaks (LF, CR, or CRLF) in a string to the HTML <br> tag.
 * @param {string} text - The string text to be formatted.
 * @returns {string} The string formatted with <br> tags.
 */
function formatStringsIntoHtmlText(text)
{
    if (typeof text !== 'string')
    {
        return '';
    }
    return text.replace(/(\r\n|\n|\r)/g, '<br>');
}

/**
 * Formats a given path to respect to current Shopify Market's root URL (e.g., adding /en-us/ prefix).
 * Includes XSS protection and graceful fallbacks.
 * @param {string} path - The relative or absolute path to format.
 * @returns {string} The fully qualified, market-aware URL.
 */
window.mUrl = function(path)
{
    if (!path) return window.location.origin + '/';
    if (path.startsWith('http') || path.startsWith('//') || path.startsWith('#') || path.startsWith('tel:') || path.startsWith('mailto:')) return path;

    if (/[<>"'\\]/.test(path)) 
    {
        console.warn('Invalid characters detected in path:', path);
        return window.location.origin + '/';
    }

    if (!window.ShopifyMarket)
    {
        console.warn('ShopifyMarket object not found, using fallback URL handling');
        return path.startsWith('/') ? window.location.origin + path : window.location.origin + '/' + path;
    }

    const root = window.ShopifyMarket.root || '/';
    const cleanRoot = root === '/' ? '' : root;
    let cleanPath = path.startsWith('/') ? path : '/' + path;

    if (cleanRoot && cleanPath.startsWith(cleanRoot))
    {
        cleanPath = cleanPath.replace(cleanRoot, '');
    }

    const finalPath = cleanRoot + cleanPath;
    return finalPath.startsWith('/') ? window.location.origin + finalPath : window.location.origin + '/' + finalPath;
};

/**
 * Fields mask utility
 * 
 * Provides functions to format input values according to different mask patterns.
 */
const MaskUtils = 
{
    formatPhone(value) 
    {
        if (isEmpty(value)) 
        {
            return "";
        }

        value = String(value).replace(/\D/g, "");
        value = value.substring(0, 15);

        return "+" + value;
    },

    formatDateDMY(value) 
    {
        if (isEmpty(value)) 
        {
            return "";
        }

        value = String(value);

        return value
            .replace(/\D/g, "")
            .replace(/(\d{2})(\d)/, "$1/$2")
            .replace(/(\d{2})(\d)/, "$1/$2")
            .replace(/(\d{4})\d+?$/, "$1");
    },

    formatDateYMD(value) 
    {
        if (isEmpty(value)) 
        {
            return "";
        }

        value = String(value);

        return value
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d)/, "$1/$2")
            .replace(/(\d{2})(\d)/, "$1/$2")
            .replace(/(\d{2})\d+?$/, "$1");
    },

    formatDateMDY(value) 
    {
        if (isEmpty(value)) 
        {
            return "";
        }

        value = String(value);

        return value
            .replace(/\D/g, "")
            .replace(/(\d{2})(\d)/, "$1/$2")
            .replace(/(\d{2})(\d)/, "$1/$2")
            .replace(/(\d{4})\d+?$/, "$1");
    },

    formatZipCode(value)
    {
        if (isEmpty(value))
        {
            return "";
        }

        value = String(value).replace(/\D/g, "");
        return value.substring(0, 10);
    },

    applyMask(type, value) 
    {
        if (isEmpty(value)) 
        {
            return "";
        }

        if (type === "phone") 
        {
            return this.formatPhone(value);
        } 
        else if (type === "date_dmy") 
        {
            return this.formatDateDMY(value);
        } 
        else if (type === "date_ymd") 
        {
            return this.formatDateYMD(value);
        } 
        else if (type === "date_mdy") 
        {
            return this.formatDateMDY(value);
        }
        else if (type === "zip") 
        {
            return this.formatZipCode(value);
        } 
        else 
        {
            return value;
        }
    },

    applyMaskToInput(type, inputId) 
    {
        const input = document.getElementById(inputId);

        if (!input) 
        {
            return;
        }

        input.addEventListener("input", () => 
        {
            input.value = this.applyMask(type, input.value);
        });
    },

    bindAll() 
    {
        const elements = document.querySelectorAll("[data-mask]");
    
        elements.forEach((el) => 
        {
            const type = el.getAttribute("data-mask");
    
            let input = el;
    
            if (input.tagName.toLowerCase() !== "input") 
            {
                input = el.querySelector("input");
            }
    
            if (!input) 
            {
                return;
            }
    
            input.addEventListener("input", () => 
            {
                input.value = this.applyMask(type, input.value);
            });
        });
    }        
};